/** The tenant page's onboarding banner (plan G13): an easy tenant whose setup has not
 *  reached Ready shows "Easy setup in progress — Continue", so a closed tab or a second ops
 *  user finds the way back. Every other onboarding-managed tenant (or an easy one that is
 *  ready) keeps the advanced "Setup N of M complete" banner. One read of the aggregate state,
 *  no polling here; a viewer without the easy permissions never asks. */
import { Alert, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import * as easyApi from '@/api/easyOnboarding';
import type { EasyPhase } from '@/api/easyOnboarding';
import { usePermissions } from '@/hooks/usePermissions';

import { SetupProgressBanner } from '../setup/SetupProgressBanner';
import { EASY_PERMS } from './useEasyOnboarding';

const PHASE_WORDS: Record<Exclude<EasyPhase, 'ready'>, string> = {
  setup: 'setting up and looking for bookings',
  choose: 'waiting for you to choose tours',
  importing: 'importing tours and sample bookings',
};

export function OnboardingBanner({ topId }: { topId: number }) {
  const { hasAll } = usePermissions();
  const canEasy = hasAll([...EASY_PERMS]);
  const q = useQuery({
    queryKey: easyApi.easyStateKey(topId),
    queryFn: ({ signal }) => easyApi.getState(topId, signal),
    enabled: canEasy,
    retry: false,
    staleTime: 10_000,
  });
  if (canEasy && q.isLoading) return null;
  const phase = q.data?.phase;
  if (phase && phase !== 'ready') {
    return (
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        action={
          <Button size="small" component={RouterLink} to={`/tour-operators/${topId}/onboard`}>
            Continue
          </Button>
        }
      >
        Easy setup in progress — {PHASE_WORDS[phase]}.
      </Alert>
    );
  }
  return <SetupProgressBanner topId={topId} />;
}
